/**
 * QnaMessageList — the chronological Rückfragen conversation (P2 Placement Slice 9, Tab 3c). Inbound
 * carrier messages are left-aligned (muted), outbound broker answers right-aligned (primary tint). A
 * long body collapses after 12 lines with «mehr anzeigen». An unread question shows a dot; a sender
 * whose domain differs shows an amber note; a bounced answer shows a red line.
 */

import { useState } from 'react';
import { QNA_UNGELESEN_LABEL, QNA_ABSENDER_ABWEICHEND_HINWEIS } from '@brokly/shared';
import type { QnaMessageEintrag } from '@brokly/shared';
import { dateTimeShortDe } from '../../lib/format';
import { cn } from '../../lib/cn';

function MessageText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const lines = text.split('\n');
  const long = lines.length > 12;
  const shown = long && !expanded ? lines.slice(0, 12).join('\n') : text;
  return (
    <div>
      <p className="whitespace-pre-wrap text-sm">{shown}</p>
      {long && (
        <button type="button" className="mt-1 text-xs text-primary underline" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'weniger anzeigen' : 'mehr anzeigen'}
        </button>
      )}
    </div>
  );
}

export function QnaMessageList({
  messages,
  onOpenMail,
}: {
  messages: QnaMessageEintrag[];
  onOpenMail: (communicationId: string) => void;
}) {
  return (
    <ol className="flex flex-col gap-3" data-testid="qna-message-list">
      {messages.map((m) => {
        const inbound = m.direction === 'versicherer_an_broker';
        const label = inbound ? m.verfasser ?? 'Versicherer' : m.verfasser ?? 'Sie';
        return (
          <li
            key={m.id}
            className={cn('flex', inbound ? 'justify-start' : 'justify-end')}
            data-testid={`qna-message-${m.id}`}
          >
            <div
              className={cn(
                'max-w-[80%] rounded-lg border p-3',
                inbound ? 'border-border bg-muted' : 'border-primary/30 bg-primary/5',
              )}
            >
              <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{label}</span>
                <span>· {dateTimeShortDe(m.created_at)}</span>
                {inbound && !m.gelesen && (
                  <span className="inline-flex items-center gap-1 text-status-info" data-testid={`qna-unread-${m.id}`}>
                    <span className="inline-block size-1.5 rounded-full bg-status-info" />
                    {QNA_UNGELESEN_LABEL}
                  </span>
                )}
              </div>
              <MessageText text={m.text} />
              {inbound && m.absender_abweichend && (
                <p role="note" className="mt-2 text-xs text-status-warning">
                  {QNA_ABSENDER_ABWEICHEND_HINWEIS}
                </p>
              )}
              {!inbound && m.bounce_at && (
                <p className="mt-2 text-xs text-status-danger" data-testid={`qna-bounce-${m.id}`}>
                  Zustellung fehlgeschlagen
                </p>
              )}
              {inbound && m.attachment_count > 0 && (
                <a
                  href={`/posteingang/${m.communication_id}`}
                  target="_blank"
                  rel="noopener"
                  className="mt-2 inline-block text-xs text-primary underline"
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenMail(m.communication_id);
                  }}
                >
                  {m.attachment_count} Beilagen
                </a>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
